import { selectSearchByQueryId } from 'ducks/searches/selectors'
import { createDoFetchInMemoryByQueryId } from 'ducks/searches/operations/createDoFetchInMemoryByQueryId'

import { doAttachementRequest } from 'ducks/requests'
import { doApiReadRequest, doApiWriteRequest } from 'ducks/requests/operations'
import {
  selectCurrentRulesById,
  selectCurrentRules,
} from 'ducks/rules/selectors'
import { buildId } from 'util/globalId'
import { omit } from 'util/objects'
import { normalize } from 'normalizr'
import { rule as ruleEntity } from 'ducks/entities/schema'

import {
  FETCH_RULES,
  FETCH_RULE,
  DELETE_RULE,
  UPDATE_RULE,
  UPLOAD_RULE_ACTION_ATTACHMENT,
} from './actionTypes'
import { rulesApiV1ResponseSchema, ruleApiV1ResponseSchema } from './schema'

const LOAD_ALL_RULES_QUERYID = 'entityType:rule pageSize:10000'

export const doFetchRulesV1 = ({ skipLoaded } = {}) => (dispatch, getState) => {
  const queryId = LOAD_ALL_RULES_QUERYID
  const cursor = 'all'
  const state = getState()
  const { loaded = null, cursors = {} } = selectSearchByQueryId(state, queryId)
  const hasCurrentPage = !!cursors[cursor]
  const isStale = hasCurrentPage && cursors[cursor].isStale

  const hasLoaded = loaded && hasCurrentPage && !isStale

  if (hasLoaded && skipLoaded) {
    // Note we might need to change this in future to return the results
    // from the previous query
    return Promise.resolve({})
  }

  return dispatch(
    doApiReadRequest(
      FETCH_RULES,
      'v1/automation_rules',
      {
        per_page: 10000,
      },
      {
        normalizationSchema: rulesApiV1ResponseSchema,
        app: true,
        searches: {
          queryId,
          cursor,
          extractPagination: 'apiv1',
        },
      }
    )
  )
}

export const doFetchRulesV1ById = (id, options = {}) => dispatch => {
  // targetStore: optional, defaults to changeEntity()'s default store otherwise
  const { targetStore } = options

  return dispatch(
    doApiReadRequest(
      FETCH_RULE,
      `v1/automation_rules/${id}`,
      {},
      {
        normalizationSchema: ruleApiV1ResponseSchema,
        moduleOptions: {
          entities: {
            targetStore,
            missingEntityActions: [
              {
                entityType: 'rule',
                // convert from gid to scatter swapped that's used in entity store
                entityId: id,
                phases: ['SUCCESS'],
              },
            ],
          },
        },
      }
    )
  )
}

export const doFetchRulesInMemory = createDoFetchInMemoryByQueryId({
  fromQueryId: LOAD_ALL_RULES_QUERYID,
  entityType: 'rule',
  doLoadAllFn: doFetchRulesV1,
})

// eslint-disable-next-line no-unused-vars
export const doDeleteSingleRule = (id, options = {}) => dispatch => {
  return dispatch(
    doApiWriteRequest(
      DELETE_RULE,
      `api/settings/rules/${buildId('Rule', id)}`,
      {},
      {
        method: 'DELETE',
        optimist: {},
        normalizationSchema: rulesApiV1ResponseSchema,
        searches: {
          additionalActions: [
            {
              type: 'INVALIDATE_ENTITIES',
              entityTypes: ['rule'],
              phases: ['SUCCESS'],
            },
          ],
        },
        moduleOptions: {
          toasts: {
            enabled: true,
            started: {
              enabled: true,
              content: 'Rule deleted',
            },
            success: {
              enabled: false,
            },
            failed: {
              content: 'Rule deletion failed',
              onClickAction: () => {
                dispatch(doDeleteSingleRule(id, options))
              },
            },
          },
          entities: {
            targetOperation: 'remove',
            additionalActions: [
              {
                // Because we're passing through a global id here, we need to manually delete
                // the entity from the store
                entityType: 'rule',
                entityId: id,
                stores: ['pending', 'current'],
                operation: 'remove',
                phases: ['STARTED'],
              },
            ],
          },
        },
      }
    )
  )
}

function calculateRulePosition(rule, currentOrderedRules) {
  if (currentOrderedRules[rule.row_order + 1]) {
    return currentOrderedRules[rule.row_order + 1].row_order - 10
  } else if (currentOrderedRules[rule.row_order - 1]) {
    return currentOrderedRules[rule.row_order - 1].row_order + 10
  }
  return rule.row_order
}

export const doUpdateSingleRule = (rule, options = {}) => async (
  dispatch,
  getState
) => {
  const id = rule.id
  const { updatePosition, queryId, orderedIds = null } = options

  const state = getState()

  const orderedRules = selectCurrentRules(state)
  const entitiesById = selectCurrentRulesById(state)
  const entity = entitiesById[id]

  const currentTriggerIds = {}
  const currentConditionsIds = {}
  const currentActionsIds = {}
  if (entity) {
    entity.triggers.forEach(tId => {
      currentTriggerIds[tId] = true
    })
    // eslint-disable-next-line no-underscore-dangle
    entity.conditions.forEach(cId => {
      currentConditionsIds[cId] = true
    })
    entity.actions.forEach(aId => {
      currentActionsIds[aId] = true
    })
  }

  const triggers = []
  const conditions = []
  const actions = []
  const uploadAttachments = []

  rule.triggers.forEach(trigger => {
    delete currentTriggerIds[trigger.id]
    triggers.push(trigger)
  })

  rule.conditions.forEach(cond => {
    delete currentConditionsIds[cond.id]
    conditions.push(cond)
  })

  rule.actions.forEach(action => {
    delete currentActionsIds[action.id]
    const messageTemplate = action.messageTemplate
    const ruleReplyTemplateId = action.rule_reply_template
    const sanitizedAction = omit(
      ['messageTemplate', 'rule_reply_template'],
      action
    )

    if (messageTemplate) {
      const { body, attachments, pendingUploads } = messageTemplate

      const actionMessageTemplate = {
        body,
      }

      if (ruleReplyTemplateId) {
        // we're updating an existing rule message template
        actionMessageTemplate.id = ruleReplyTemplateId
      }

      if (Array.isArray(attachments) && attachments.length > 0) {
        actionMessageTemplate.attachments = attachments
      }

      if (Array.isArray(pendingUploads) && pendingUploads.length > 0) {
        uploadAttachments.push(...pendingUploads)
      }

      sanitizedAction.rule_reply_template_attributes = actionMessageTemplate
      // keep action.value to be the same as message template body so it's backwards compatible with old settings screen
      sanitizedAction.value = body
    }

    actions.push(sanitizedAction)
  })

  // API V0 expects deletes to be sent a an array of objects with
  // id and _delete=1
  Object.keys(currentTriggerIds).forEach(triggerId => {
    if (triggerId && triggerId !== 'undefined') {
      triggers.push({
        // eslint-disable-next-line no-underscore-dangle
        _destroy: 'true',
        id: triggerId,
      })
    }
  })
  Object.keys(currentConditionsIds).forEach(conditionId => {
    if (conditionId && conditionId !== 'undefined') {
      conditions.push({
        // eslint-disable-next-line no-underscore-dangle
        _destroy: 'true',
        id: conditionId,
      })
    }
  })
  Object.keys(currentActionsIds).forEach(actionId => {
    if (actionId && actionId !== 'undefined') {
      actions.push({
        // eslint-disable-next-line no-underscore-dangle
        _destroy: 'true',
        id: actionId,
      })
    }
  })

  const payload = {
    id,
    active: rule.active,
    match_type: rule.match_type,
    name: rule.name,
    rule_actions_attributes: actions,
    rule_conditions_attributes: conditions,
    rule_triggers_attributes: triggers,
  }
  if (updatePosition) {
    payload.row_order_position = rule.position
  }

  const optimisticRule = {
    ...rule,
  }
  if (updatePosition) {
    optimisticRule.position = calculateRulePosition(rule, orderedRules)
  }

  const normalizedEntities = normalize(rule, ruleEntity)

  const optimist = {
    entities: normalizedEntities.entities,
  }

  const response = await dispatch(
    doApiWriteRequest(
      UPDATE_RULE,
      `v1/automation_rules/${rule.id}`,
      { rule: payload },
      {
        method: 'PUT',
        optimist,
        normalizationSchema: ruleApiV1ResponseSchema,
        searches: {
          additionalActions: [
            {
              type: 'INVALIDATE_ENTITIES',
              entityTypes: Object.keys(optimist.entities),
              phases: ['SUCCESS'],
            },
            {
              type: 'UPDATE_CURSOR',
              queryId,
              entityIds: orderedIds,
              phases: ['STARTED'],
            },
          ],
        },
        moduleOptions: {
          entities: {
            additionalActions: Object.keys(optimist.entities).map(
              entityType => {
                return {
                  entityType,
                  stores: ['pending'],
                  phases: ['SUCCESS'],
                  type: 'clear',
                }
              }
            ),
          },
          toasts: {
            enabled: true,
            started: {
              enabled: false,
            },
            success: {
              enabled: true,
              content: 'Rule updated',
            },
            failed: {
              content: 'Rule update failed',
              onClickAction: () => {
                dispatch(doUpdateSingleRule(rule, options))
              },
            },
          },
        },
      }
    )
  )

  if (uploadAttachments.length > 0) {
    await dispatch(
      doAttachementRequest(
        UPLOAD_RULE_ACTION_ATTACHMENT,
        `/templates/${id}/attachments`,
        uploadAttachments.map(upload => upload.editorFile),
        options
      )
    )
  }

  return response
}

export const doCreateSingleRule = (rule, options = {}) => async dispatch => {
  const id = rule.id

  const actions = []
  const uploadAttachments = []

  rule.actions.forEach(action => {
    const messageTemplate = action.messageTemplate
    const ruleReplyTemplateId = action.rule_reply_template
    const sanitizedAction = omit(
      ['messageTemplate', 'rule_reply_template'],
      action
    )

    if (messageTemplate) {
      const { body, attachments, pendingUploads } = messageTemplate

      const actionMessageTemplate = {
        body,
      }

      if (ruleReplyTemplateId) {
        // we're updating an existing rule message template
        actionMessageTemplate.id = ruleReplyTemplateId
      }

      if (Array.isArray(attachments) && attachments.length > 0) {
        actionMessageTemplate.attachments = attachments
      }

      if (Array.isArray(pendingUploads) && pendingUploads.length > 0) {
        uploadAttachments.push(...pendingUploads)
      }

      sanitizedAction.rule_reply_template_attributes = actionMessageTemplate
      // keep action.value to be the same as message template body so it's backwards compatible with old settings screen
      sanitizedAction.value = body
    }

    actions.push(sanitizedAction)
  })

  const payload = {
    id,
    active: rule.active,
    match_type: rule.match_type,
    name: rule.name,
    rule_actions_attributes: actions,
    rule_conditions_attributes: rule.conditions,
    rule_triggers_attributes: rule.triggers,
  }

  const normalizedEntities = normalize(rule, ruleEntity)

  const optimist = {
    entities: normalizedEntities.entities,
  }

  const response = await dispatch(
    doApiWriteRequest(
      UPDATE_RULE,
      `v1/automation_rules`,
      { rule: payload },
      {
        method: 'POST',
        optimist,
        normalizationSchema: ruleApiV1ResponseSchema,
        searches: {
          additionalActions: [
            {
              type: 'INVALIDATE_ENTITIES',
              entityTypes: ['rule'],
              phases: ['SUCCESS'],
            },
          ],
        },
        moduleOptions: {
          entities: {
            additionalActions: Object.keys(optimist.entities).map(
              entityType => {
                return {
                  entityType,
                  stores: ['pending'],
                  phases: ['SUCCESS'],
                  type: 'clear',
                }
              }
            ),
          },
          toasts: {
            enabled: true,
            started: {
              enabled: false,
            },
            success: {
              enabled: true,
              content: 'Rule created',
            },
            failed: {
              content: 'Rule create failed',
              onClickAction: () => {
                dispatch(doCreateSingleRule(rule, options))
              },
            },
          },
        },
        ...options,
      }
    )
  )

  const ruleId = response?.id || response?.automation_rule?.id
  if (ruleId && uploadAttachments.length > 0) {
    await dispatch(
      doAttachementRequest(
        UPLOAD_RULE_ACTION_ATTACHMENT,
        `/templates/${ruleId}/attachments`,
        uploadAttachments.map(upload => upload.editorFile),
        options
      )
    )
  }

  return dispatch(doFetchRulesV1())
}

export const doDeleteRules = (ids, options = {}) => dispatch => {
  // Promise.all is not technically required in the current implementation because
  // we'll only step into this block if the ids array has a single id, but I do
  // want to leave the door open for us to say that deleting less than X rows uses
  // single deletes instead of batch deletes
  return Promise.all(ids.map(id => dispatch(doDeleteSingleRule(id, options))))
}
